import React, { Fragment } from "react"
import { Col, Modal, Row } from "react-bootstrap"
import cloneDeep from "lodash.clonedeep"
import get from "lodash.get"
import axios from "axios"

import FormInput from "@/_components/FormInput"
import CustomButton from "@/_components/CustomButton"
import FormInputExtended from "@/_components/FormInputExtended"
import FormBulletSummary from "@/_components/FormBulletSummary"
import { customEvents, labelFromName } from "@/_services/utils"
import { PROPS_, replaceVariables } from "basikon-common-utils"
import { addOops } from "@/_services/notification"
import { loc } from "@/_services/localization"

class WorkflowCommentModal extends React.Component {
  constructor(props) {
    super(props)

    const { transition = {}, entity = {} } = this.props
    const { reasons: modalReasons } = transition.showCommentModal || {}

    // The modal config can dynamically computed by an external source.
    // For security we only allow calls to the platform.
    const getReasonsFromApi = typeof modalReasons === "string" && modalReasons.startsWith("/api/")

    this.state = {
      // We need to use the entity to support the replacement syntax like in this select example:
      // { select: "/api/script/runs/customized-list?type=unfundedFees&registration=:registration" }
      // This is also need to assigning default value.
      clonedEntity: cloneDeep(entity),
      reasons: (getReasonsFromApi ? [] : modalReasons) || [],
      loadingReasons: false,
      errors: [],
      getReasonsFromApi,
      modalConfigFromApi: null,
    }
  }

  componentDidMount() {
    this.getReasons()
  }

  getReasons = async () => {
    const { clonedEntity = {}, getReasonsFromApi } = this.state
    const { transition = {}, onClose } = this.props
    const { reasons = [], method = "POST", comment, message } = transition.showCommentModal || {}

    let showModal
    const statePatch = { reasons, clonedEntity, loadingReasons: false }
    if (getReasonsFromApi) {
      try {
        this.setState({ loadingReasons: true })

        const url = reasons
        // replace the reasons string containing the URL by an array
        statePatch.reasons = []

        const { data } = await axios({ url: replaceVariables(url, clonedEntity), method, data: clonedEntity })

        if (comment || data.comment || message || data.message) {
          showModal = true
          if (comment || data.comment) statePatch.clonedEntity.comment = ""
        }

        let apiReasons = []
        if (Array.isArray(data)) apiReasons = data
        if (typeof data === "object") {
          if (Array.isArray(data.reasons)) apiReasons = data.reasons
          statePatch.modalConfigFromApi = Object.assign({}, transition.showCommentModal, data)
        }

        for (let i = 0; i < apiReasons.length; i++) {
          const reason = apiReasons[i] || {}

          const hasFormInputPropsValue = reason.formInputProps?.value !== undefined
          if (reason.defaultValue !== undefined || hasFormInputPropsValue) {
            const patchKey = reason.formInputProps?.field ?? reason.value
            statePatch.clonedEntity[patchKey] = reason.defaultValue ?? reason.formInputProps?.value
            showModal = true

            // We use the value to initialize the reason but we must delete it,
            // because there are not re-renders the users would not be able to change it.
            if (hasFormInputPropsValue) delete reason.formInputProps?.value
          }

          if (reason.value === undefined && reason.formInputProps?.field) {
            reason.value = reason.formInputProps.field
          }

          statePatch.reasons.push({
            ...reason,
            value: reason.value ?? reason.registration ?? reason._id,
            label: reason.label ?? reason.name,
          })
        }
      } catch (error) {
        addOops(error)
      }
    } else {
      if (comment || message) {
        showModal = true
        if (comment) statePatch.clonedEntity.comment = ""
      }

      for (let i = 0; i < reasons.length; i++) {
        const reason = reasons[i]

        const hasFormInputPropsValue = reason.formInputProps?.value !== undefined
        if (reason.defaultValue !== undefined || hasFormInputPropsValue) {
          const patchKey = reason.formInputProps?.field ?? reason.value
          statePatch.clonedEntity[patchKey] = reason.defaultValue ?? reason.formInputProps?.value
          showModal = true

          if (hasFormInputPropsValue) delete reason.formInputProps?.value
        }

        if (reason.value === undefined && reason.formInputProps?.field) {
          reason.value = reason.formInputProps.field
        }
      }
    }

    // the modal can be used without reasons,
    // with just a message in its title and the buttons
    if (statePatch.reasons?.length || showModal) {
      this.setState(statePatch)
    } else if (getReasonsFromApi) {
      // we need to return something truthy in order to continue processing the transition
      // because returning nothing means that the modal has been dismissed by the user without action
      onClose([])
    }
  }

  handleClose = async ({ save } = {}) => {
    const { modalConfigFromApi } = this.state
    const { transition = {}, onClose } = this.props
    const { checkListMode, singleSelection = false } = modalConfigFromApi || transition.showCommentModal || {}

    if (!save) return onClose()

    const { clonedEntity, reasons } = this.state
    const stateReasons = cloneDeep(reasons)
    let selectedReasons = []
    const errors = []
    const errorsModelFieldPath = []

    if (checkListMode) {
      // The user has to check all checkbox to validate the modal.
      // In this mode we don't retain the reasons so don't assign them,
      // we just want to make sure users review the checklist
      const unchecked = stateReasons.filter(it => !clonedEntity[it.value])
      if (unchecked.length > 0) {
        this.setState({ errors: ["All checkboxes must be checked"] })
        return
      }
    } else {
      if (singleSelection) {
        const selectedReason = stateReasons.find(p => p.value?.toString() === clonedEntity.reason?.toString())

        if (selectedReason) {
          selectedReason.data ??=
            get(clonedEntity, selectedReason.formInputProps?.field || selectedReason.value) ||
            (clonedEntity?.reasons || []).find(r => r === selectedReason.value) ||
            selectedReason.value
          selectedReasons = [selectedReason]
        } else {
          this.setState({ errors: ["At least one option must be selected"] })
          return
        }
      } else {
        // WARNING: this is necessary to append the data object to each reason to have more room for configuration.
        // It is also used in many implementation scripts like entity-force-status
        selectedReasons = stateReasons
          .map(reason => {
            reason.data =
              get(clonedEntity, reason.formInputProps?.field || reason.value) || (clonedEntity?.reasons || []).find(r => r === reason.value)
            reason.value ??= reason.data
            reason.label ??= reason.formInputProps?.label

            if (reason.formInputProps) {
              if (reason.formInputProps.mandatory && (!reason.data || (Array.isArray(reason.data) && !reason.data.length))) {
                const errorFormat =
                  reason.formInputProps.label || reason.label
                    ? loc(reason.formInputProps.label || reason.label) + " " + loc("is mandatory")
                    : loc("Field is mandatory")
                reason.formInputProps.errorFormat = errorFormat
                const modelFieldPath = reason.formInputProps.modelFieldPath || reason.formInputProps.field || reason.value
                const path = `${PROPS_}${modelFieldPath}`
                clonedEntity[path] = { errorFormat }
                errorsModelFieldPath.push({ modelFieldPath })
                errors.push(errorFormat)
              } else if (reason.formInputProps.errorFormat) {
                reason.formInputProps.errorFormat = undefined
                clonedEntity[`${PROPS_}${reason.formInputProps?.field || reason.value}`] = { errorFormat: undefined }
              }
            }

            return reason
          })
          .filter(reason => reason.data)
      }
      // The comment property is inserted at the end of the selectedReasons array.
      if (clonedEntity.comment) {
        selectedReasons.push({ value: "comment", label: clonedEntity.comment, data: clonedEntity.data ?? clonedEntity.comment })
      }
    }

    if (errors.length) {
      this.setState({ errors })
      if (errorsModelFieldPath.length) {
        window.dispatchEvent(new CustomEvent(customEvents.formInput.forceComponentUpdate, { detail: { errors: errorsModelFieldPath } }))
      }
      return
    }

    onClose(
      selectedReasons.map(reason => {
        const { value, label, defaultValue, data } = reason
        // Cleanup to avoid sending useless data over the network.
        // Automated tests use this fields order so update them if you change it.
        return {
          value,
          label,
          data,
          defaultValue,
        }
      }),
    )
  }

  handleSetState = patch => {
    const { clonedEntity } = this.state
    const statePatch = { clonedEntity: { ...clonedEntity, ...patch } }
    this.setState(statePatch)
  }

  render() {
    const { loadingReasons, reasons, errors, modalConfigFromApi, clonedEntity } = this.state
    const { transition } = this.props
    const { showCommentModal } = transition

    if (!showCommentModal || loadingReasons || !Array.isArray(reasons)) {
      return null
    }

    const {
      title = "Comments",
      singleSelection = false,
      saveButtonTitle,
      closeButtonTitle,
      listTitle,
      displayWidth,
      comment,
      message,
    } = modalConfigFromApi || showCommentModal

    const useSelectThreshold = reasons?.find(it => it.formInputProps) ? 100000 : 10

    // There can be no reasons,
    // like when the modal is used for confirmation of the transition only.
    const nbOfReasons = reasons.length

    return (
      <Modal
        show={true}
        onHide={() => this.handleClose()}
        backdrop="static"
        className="workflow-comment-modal"
        // same property as the workflow document modal
        bsSize={displayWidth || ""}
      >
        <Modal.Header closeButton>
          <Modal.Title>{loc(title)}</Modal.Title>
        </Modal.Header>

        {(nbOfReasons > 0 || comment || message) && (
          <Modal.Body>
            <Row>
              <Col xs={12}>
                {nbOfReasons <= useSelectThreshold && (
                  <Row>
                    {reasons.map((reason, key) => {
                      const { colProps: reasonColProps } = reason
                      const colProps = reasonColProps || { xs: 12 }

                      return (
                        <Fragment key={key}>
                          {singleSelection ? (
                            <FormInput
                              colProps={colProps}
                              showLabel={false}
                              formGroupClassName="mb-0"
                              type="radio"
                              obj={clonedEntity}
                              field="reason"
                              option={reason.value.toString()}
                              label={reason.label || labelFromName(reason.value)}
                              onSetState={this.handleSetState}
                            />
                          ) : (
                            <FormInputExtended
                              colProps={colProps}
                              obj={clonedEntity}
                              field={reason.value}
                              label={reason.label || labelFromName(reason.value)}
                              onSetState={this.handleSetState}
                              type="checkbox"
                              showLabel={false}
                              // this must be last to allow overriding the props
                              {...reason.formInputProps}
                            />
                          )}
                        </Fragment>
                      )
                    })}
                  </Row>
                )}

                {nbOfReasons > useSelectThreshold && singleSelection && (
                  <FormInput obj={clonedEntity} field="reason" select={reasons} onSetState={this.handleSetState} label={listTitle || "Reason"} />
                )}

                {nbOfReasons > useSelectThreshold && !singleSelection && (
                  <FormInputExtended
                    multiple
                    obj={clonedEntity}
                    field="reasons"
                    select={reasons}
                    onSetState={this.handleSetState}
                    label={listTitle || "Reasons"}
                  />
                )}

                {comment && <FormInput obj={clonedEntity} field="comment" type="textarea" onSetState={this.handleSetState} />}

                {message ? loc(message) : null}
              </Col>
            </Row>
          </Modal.Body>
        )}

        <Modal.Footer>
          <FormBulletSummary messages={errors} />

          <CustomButton bsSize="sm" onClick={() => this.handleClose()}>
            {closeButtonTitle ? loc(closeButtonTitle) : loc`Close`}
          </CustomButton>

          <CustomButton bsStyle="primary" bsSize="sm" fill onClick={() => this.handleClose({ save: true })}>
            {saveButtonTitle ? loc(saveButtonTitle) : loc`Save`}
          </CustomButton>
        </Modal.Footer>
      </Modal>
    )
  }
}

export default WorkflowCommentModal
